import axiosInstance from "./axiosInstance";
import { apiErrorText } from "../utils/apiError";

// Methods where the body is passed via the config object instead of as a positional arg
const BODY_IN_CONFIG = new Set(["get", "head", "delete", "options"]);

/**
 * Shared request helper. Every public method delegates here.
 *
 * @param {"get"|"post"|"put"|"patch"|"delete"|"head"|"options"} method
 * @param {string}  path     — API path (relative to axiosInstance baseURL)
 * @param {string}  token    — Bearer token
 * @param {string}  userOid  — user OID for the x-de-hub-obo-id audit header
 * @param {object}  [data]   — request body (ignored for GET/HEAD/OPTIONS)
 * @param {object}  [extraConfig] — any additional axios config (params, timeout, etc.)
 */
const _request = async (
  method,
  path,
  token,
  userOid,
  data = {},
  extraConfig = {}
) => {
  if (!token) {
    throw new Error("Token is missing. Please provide a valid token.");
  }

  // For FormData uploads, omit Content-Type so axios auto-sets it with the
  // correct multipart boundary. Setting "multipart/form-data" without a
  // boundary breaks FastAPI's multipart parser (-> 422).
  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData;
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    "x-de-hub-obo-id": userOid,
    ...extraConfig.headers,
  };

  const config = { ...extraConfig, headers };

  try {
    const response = BODY_IN_CONFIG.has(method)
      ? await axiosInstance[method](path, { ...config, data })
      : await axiosInstance[method](path, data, config);
    return response;
  } catch (error) {
    console.error(
      `Error in ${method.toUpperCase()} ${path}:`,
      error.response || error
    );
    // Restate the failure in the error's own `message` before it leaves here.
    // Axios sets "Request failed with status code 400", and that string is what
    // any caller which only reads `err.message` shows the user — notably the
    // library's WigMetadataCrudPanel "Save failed" dialog, whose formatter
    // doesn't know de-hub's `{field, message}` 400 shape and therefore fell
    // through to it. A rejected tract save that the server had already
    // explained ("Invalid county: 'UNKNOWN'") reached the user as a bare status
    // code with nothing to act on.
    //
    // `error.response` is left intact, so callers that parse the body
    // themselves (apiErrorText, dealsUpload's rejected-row table) are
    // unaffected — this only upgrades the fallback.
    try {
      const text = apiErrorText(error, { fallback: error.message });
      if (text) error.message = text;
    } catch {
      // Formatting must never mask the real failure.
    }
    throw error;
  }
};

const dataService = {
  searchEndpoint: (path, token, userOid, data) =>
    _request("post", path, token, userOid, data),

  postEndpoint: (path, token, userOid, data) =>
    _request("post", path, token, userOid, data),

  addEndpoint: (path, token, userOid, data) =>
    _request("post", path, token, userOid, data),

  updateEndpoint: (path, token, userOid, data) =>
    _request("put", path, token, userOid, data),

  getEndpoint: (path, token, userOid) =>
    _request("get", path, token, userOid),

  deleteEndpoint: (path, token, userOid, data) =>
    _request("delete", path, token, userOid, data),

  /**
   * Drop every cached GET response.
   *
   * `axiosInstance` wraps axios with axios-cache-interceptor, which caches all
   * GETs (default TTL 5 minutes) and does NOT invalidate anything when a
   * POST/PUT/DELETE goes out. After a destructive mutation the next list GET
   * would otherwise be served from cache and still show the deleted row.
   * Callers that delete data should await this before re-fetching.
   *
   * Blunt on purpose — it clears the whole store rather than guessing cache
   * keys. Safe: everything in it is a re-fetchable GET response.
   *
   * @returns {Promise<void>}
   */
  clearCache: async () => {
    try {
      await axiosInstance.storage?.clear?.();
    } catch (error) {
      // A cache that refuses to clear must not fail the user's action; the
      // worst case is a stale list until the TTL expires.
      console.warn("Failed to clear the request cache:", error?.message);
    }
  },

  /**
   * Generic escape hatch for any HTTP method or custom config.
   * Example: dataService.request("patch", "/api/foo", token, oid, payload)
   * Example: dataService.request("get", "/api/bar", token, oid, {}, { params: { q: "test" } })
   */
  request: (method, path, token, userOid, data, extraConfig) =>
    _request(method, path, token, userOid, data, extraConfig),
};

export default dataService;
